const BRAIN_GAME_LEVEL_CONFIG = Object.freeze({
    'memory-match': { defaultLevel: 5, minLevel: 1, maxLevel: 5 },
    'memory-matrix': { defaultLevel: 1, minLevel: 1, maxLevel: 18 },
    'sequence-repeat': { defaultLevel: 1, minLevel: 1, maxLevel: 12 },
    'symbol-n-back': { defaultLevel: 2, minLevel: 1, maxLevel: 3 },
    'stroop-colors': { defaultLevel: 3, minLevel: 1, maxLevel: 3 },
    'schulte-table': { defaultLevel: 3, minLevel: 1, maxLevel: 3 },
    'visual-search': { defaultLevel: 4, minLevel: 1, maxLevel: 4 },
    'flanker-direction': { defaultLevel: 1, minLevel: 1, maxLevel: 16 },
    'go-no-go': { defaultLevel: 4, minLevel: 1, maxLevel: 8 },
    'trail-making': { defaultLevel: 4, minLevel: 1, maxLevel: 4 },
    'chance-cards': { defaultLevel: 1, minLevel: 1, maxLevel: 10 },
    'turn-match': { defaultLevel: 1, minLevel: 1, maxLevel: 6 },
    'number-rain': { defaultLevel: 1, minLevel: 1, maxLevel: 21 },
    'order-rush': { defaultLevel: 1, minLevel: 1, maxLevel: 3 },
    'estimate-duel': { defaultLevel: 1, minLevel: 1, maxLevel: 3 },
    'new-catch': { defaultLevel: 1, minLevel: 1, maxLevel: 16 },
    'object-shelf': { defaultLevel: 1, minLevel: 1, maxLevel: 13 },
    'balance-beam': { defaultLevel: 1, minLevel: 1, maxLevel: 20 },
    'percent-pop': { defaultLevel: 3, minLevel: 1, maxLevel: 3 },
    'orbit-tracker': { defaultLevel: 1, minLevel: 1, maxLevel: 17 },
    'tone-order': { defaultLevel: 1, minLevel: 1, maxLevel: 2 },
    'focus-blink': { defaultLevel: 1, minLevel: 1, maxLevel: 15 },
    'rule-matrix': { defaultLevel: 1, minLevel: 1, maxLevel: 6 },
    'tower-plan': { defaultLevel: 1, minLevel: 1, maxLevel: 4 },
    'source-bind': { defaultLevel: 1, minLevel: 1, maxLevel: 13 },
    'compass-route': { defaultLevel: 1, minLevel: 1, maxLevel: 6 },
    'line-judge': { defaultLevel: 1, minLevel: 1, maxLevel: 17 },
    'dual-track': { defaultLevel: 4, minLevel: 1, maxLevel: 9 },
});

const getBrainGameLevelConfig = (gameId) => BRAIN_GAME_LEVEL_CONFIG[`${gameId || ''}`.trim()] || null;

const LEVEL_DIFFICULTY_TIERS = Object.freeze(['easy', 'medium', 'hard', 'expert']);

const getLevelDifficultyTier = (value, minimum = 0, maximum = 100) => {
    const parsedMinimum = Number(minimum);
    const parsedMaximum = Number(maximum);

    if (!Number.isFinite(parsedMinimum) || !Number.isFinite(parsedMaximum)) {
        return LEVEL_DIFFICULTY_TIERS[0];
    }

    const lowerBound = Math.min(parsedMinimum, parsedMaximum);
    const upperBound = Math.max(parsedMinimum, parsedMaximum);

    if (upperBound === lowerBound) {
        return LEVEL_DIFFICULTY_TIERS[0];
    }

    const parsedLevel = value === null || value === '' ? Number.NaN : Number(value);
    const roundedLevel = Number.isFinite(parsedLevel) ? Math.round(parsedLevel) : lowerBound;
    const clampedLevel = Math.max(lowerBound, Math.min(upperBound, roundedLevel));
    const progress = (clampedLevel - lowerBound) / (upperBound - lowerBound);

    if (progress <= 0.25) {
        return LEVEL_DIFFICULTY_TIERS[0];
    }

    if (progress <= 0.5) {
        return LEVEL_DIFFICULTY_TIERS[1];
    }

    if (progress <= 0.75) {
        return LEVEL_DIFFICULTY_TIERS[2];
    }

    return LEVEL_DIFFICULTY_TIERS[3];
};

const normalizeBrainGameLevel = (gameId, value) => {
    const config = getBrainGameLevelConfig(gameId);

    if (!config) {
        return null;
    }

    const parsedLevel = value === null || value === '' ? Number.NaN : Number(value);
    const level = Number.isFinite(parsedLevel) ? Math.round(parsedLevel) : config.defaultLevel;

    return Math.max(config.minLevel, Math.min(config.maxLevel, level));
};

const createBrainGameLevels = (rawLevels = {}) => Object.fromEntries(
    Object.keys(BRAIN_GAME_LEVEL_CONFIG).map((gameId) => [
        gameId,
        normalizeBrainGameLevel(gameId, rawLevels?.[gameId]),
    ])
);

const setBrainGameLevel = (levels, gameId, value) => {
    const normalizedGameId = `${gameId || ''}`.trim();
    const level = normalizeBrainGameLevel(normalizedGameId, value);

    if (level === null) {
        return levels;
    }

    return {
        ...createBrainGameLevels(levels),
        [normalizedGameId]: level,
    };
};

module.exports = {
    BRAIN_GAME_LEVEL_CONFIG,
    LEVEL_DIFFICULTY_TIERS,
    createBrainGameLevels,
    getBrainGameLevelConfig,
    getLevelDifficultyTier,
    normalizeBrainGameLevel,
    setBrainGameLevel,
};
